import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import type { Review } from '@limo/database'
import {
  REVIEW_SOURCES,
  REVIEW_SOURCE_KEYS,
  DEFAULT_REVIEW_SOURCE,
} from '@limo/shared'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { StarRating } from '@/components/ui/star-rating'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

const reviewSchema = z.object({
  reviewer_name: z.string().min(1, 'Name is required'),
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Review content is required'),
  rating: z.number().min(1, 'Rating is required').max(5),
  source: z.enum(REVIEW_SOURCE_KEYS),
})

type ReviewFormData = z.infer<typeof reviewSchema>

interface ReviewDialogProps {
  reviewId: string | null
  accountId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ReviewDialog({
  reviewId,
  accountId,
  open,
  onOpenChange,
}: ReviewDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const isEditing = !!reviewId

  const { data: review, isLoading: isLoadingReview } = useQuery({
    queryKey: ['review', reviewId],
    queryFn: async () => {
      if (!reviewId) return null
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('id', reviewId)
        .single()
      if (error) throw error
      return data as Review
    },
    enabled: !!reviewId && open,
  })

  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      reviewer_name: '',
      title: '',
      content: '',
      rating: 5,
      source: DEFAULT_REVIEW_SOURCE,
    },
  })

  useEffect(() => {
    if (review) {
      form.reset({
        reviewer_name: review.reviewer_name,
        title: review.title,
        content: review.content,
        rating: review.rating,
        // Fall back to the default for rows predating the source column
        source:
          (REVIEW_SOURCE_KEYS as readonly string[]).includes(review.source)
            ? (review.source as ReviewFormData['source'])
            : DEFAULT_REVIEW_SOURCE,
      })
    } else if (!isEditing && open) {
      form.reset({
        reviewer_name: '',
        title: '',
        content: '',
        rating: 5,
        source: DEFAULT_REVIEW_SOURCE,
      })
    }
  }, [review, form, isEditing, open])

  const createMutation = useMutation({
    mutationFn: async (data: ReviewFormData) => {
      const { error } = await supabase.from('reviews').insert({
        ...data,
        account_id: accountId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      toast({
        title: 'Review created',
        description: 'The review has been successfully created.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: ReviewFormData) => {
      if (!reviewId) throw new Error('Review ID is required')
      const { error } = await supabase
        .from('reviews')
        .update(data)
        .eq('id', reviewId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      queryClient.invalidateQueries({ queryKey: ['review', reviewId] })
      toast({
        title: 'Review updated',
        description: 'The review has been successfully updated.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: ReviewFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
    }
    onOpenChange(open)
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Review' : 'Add Review'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the review information.'
              : 'Add a new customer review.'}
          </DialogDescription>
        </DialogHeader>

        {isEditing && isLoadingReview ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="reviewer_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reviewer Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Smith" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {REVIEW_SOURCES.map((source) => (
                          <SelectItem key={source.key} value={source.key}>
                            {source.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The platform this review came from. Its logo is shown on your
                      website beside the reviewer.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Amazing service!" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rating</FormLabel>
                    <FormControl>
                      <StarRating
                        rating={field.value}
                        onChange={field.onChange}
                        size="lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Review</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Write the review content..."
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEditing ? 'Update Review' : 'Add Review'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
